import { Component, Element, Host, Prop, State, h } from '@stencil/core';
import { syQuerySelector } from '../../../utils/dom.utils';
import { type UserAPI, getAPI } from '@sydle/ui-api';

@Component({
  tag: 'sy-blog-cta-suggestion',
  styleUrl: './scss/index.scss',
  shadow: true,
})
export class SyBlogCtaSuggestion {
  @Element() el: HTMLSyBlogCtaSuggestionElement;
  @State() suggestion: string;

  /**
   * Determines the display style of the component.
   * Options are 'box' for a boxed display on the page or 'floating' for a floating button.
   */
  @Prop({ reflect: true }) type: 'box' | 'floating' = 'box';

  /**
   * The header text for the Call-To-Action (CTA) section.
   */
  @Prop() header: string;

  /**
   * The message to be displayed in the CTA section.
   */
  @Prop() message: string;

  /**
   * The label for the CTA button.
   */
  @Prop() buttonLabel: string;

  /**
   * The icon to be displayed alongside the CTA button.
   */
  @Prop() buttonCtaIcon = 'emoji_objects';

  /**
   * The icon to be displayed on the send button in the suggestion modal.
   */
  @Prop() buttonSendIcon = 'send-email-3';

  /**
   * The message to be displayed on the floating button.
   */
  @Prop() floatingMessage = 'Send suggestion';

  /**
   * Determines whether the component should be hidden on mobile devices.
   */
  @Prop({ reflect: true }) hideOnMobile = false;

  /**
   * The title of the suggestion dialog.
   */
  @Prop() dialogTitle = 'Send us your suggestion!';

  /**
   * The placeholder text in the suggestion dialog input field.
   */
  @Prop() dialogPlaceholder = 'Suggestion';

  /**
   * The text to be displayed on the send button in the suggestion dialog.
   */
  @Prop() dialogSendMessage = 'Send';

  /**
   * The message displayed in the suggestion dialog body.
   */
  @Prop() dialogMessage =
    '<b>Participate</b> in blog content creation! Your opinion is essential to help us bring <b>interesting</b> and <b>relevant</b> topics.';

  /**
   * The classification for the suggestion ticket.
   */
  @Prop() ticketClassification: string;

  /**
   * The title of the response dialog after sending the suggestion.
   */
  @Prop() dialogResponseTitle: string;

  /**
   * The message in the response dialog after sending the suggestion.
   */
  @Prop() dialogResponseMessage: string;

  /**
   * Opens the suggestion modal dialog.
   */
  private handleSuggestion() {
    const modal =
      (syQuerySelector('sy-blog-suggestion-modal') as HTMLSyBlogSuggestionModalElement) ||
      document.createElement('sy-blog-suggestion-modal');

    modal.dialogTitle = this.dialogTitle;
    modal.dialogPlaceholder = this.dialogPlaceholder;
    modal.dialogSendMessage = this.dialogSendMessage;
    modal.dialogMessage = this.dialogMessage;
    modal.buttonSendIcon = this.buttonSendIcon;
    modal.ticketClassification = this.ticketClassification;
    modal.dialogResponseTitle = this.dialogResponseTitle;
    modal.dialogResponseMessage = this.dialogResponseMessage;

    document.body.appendChild(modal);
    modal.open();
  }

  /**
   * Sends the suggestion.
   */
  private sendSuggestion() {
    const modal =
      (syQuerySelector('sy-blog-alert-modal') as HTMLSyBlogSuggestionModalElement) ||
      document.createElement('sy-blog-alert-modal');

    window.blogFacade
      ?.sendSuggestion({ ticketClassification: this.ticketClassification, message: this.suggestion })
      .then((response) => {
        if (response.error) {
          console.error(response.error);
        } else {
          modal.dialogTitle = this.dialogResponseTitle;
          modal.dialogMessage = this.dialogResponseMessage;
          modal.open();
        }
      });
  }

  /**
   * Lifecycle method that gets called just after the component is first rendered.
   */
  componentDidLoad() {
    if (getAPI<UserAPI>('userAPI')?.getUser() && sessionStorage.getItem('suggestion')) {
      this.suggestion = sessionStorage.getItem('suggestion') || '';
      this.sendSuggestion();
      sessionStorage.removeItem('suggestion');
    }
  }

  /**
   * Renders the component.
   */
  render() {
    return (
      <Host>
        <sy-blog-alert-modal />
        {this.type === 'box' ? (
          <div class="card">
            <div class="card-content">
              <div class="cta-text-content">
                <sy-text type="h4">{this.header}</sy-text>
                <div class="text-body">
                  <sy-text type="body">{this.message}</sy-text>
                </div>
              </div>
              <div class="cta-button">
                <sy-button
                  content="icon-right"
                  icon={this.buttonCtaIcon}
                  id="suggestion-modal"
                  variant="filled"
                  color="secondary"
                  label={this.buttonLabel}
                  onClick={() => this.handleSuggestion()}
                />
              </div>
            </div>
          </div>
        ) : (
          <div class="floatingButton" onClick={() => this.handleSuggestion()}>
            <div class="label">
              <sy-text>{this.floatingMessage}</sy-text>
            </div>
            <div class="button">
              <sy-icon name="emoji_objects" />
            </div>
          </div>
        )}
      </Host>
    );
  }
}
